import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";
import { PrismAsyncLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/cjs/styles/prism";
import BaseContainer from "../containers/BaseContainer";

export default function PostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<{
    content: string;
    title: string;
    date: string;
    cover?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPost() {
      try {
        setLoading(true);
        const response = await fetch(`/posts/${slug}.md`);

        if (!response.ok) {
          throw new Error(`Failed to fetch post: ${response.statusText}`);
        }

        const markdown = await response.text();

        const frontmatterRegex = /^---\n([\s\S]*?)\n---\n/;
        const match = markdown.match(frontmatterRegex);

        if (match) {
          const frontmatter = match[1];
          const content = markdown.replace(frontmatterRegex, "");

          const title = frontmatter.match(/title:\s*(.*)/)?.[1] || "Untitled";
          const date =
            frontmatter.match(/date:\s*(.*)/)?.[1] || new Date().toISOString();
          const cover = frontmatter.match(/cover:\s*(.*)/)?.[1] || undefined;

          document.title = `${title} | Matteo Aries`;
          setPost({ content, title, date, cover });
        } else {
          setPost({
            content: markdown,
            title: "Untitled",
            date: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <span className="text-lg font-bold animate-pulse">Loading...</span>
      </div>
    );
  }

  return (
    <BaseContainer>
      <div className="flex flex-col items-center py-8">
        {/* Back link */}
        <div className="w-full max-w-2xl mb-6">
          <a
            className="text-sm text-gray-400 hover:text-orange-600 transition-colors"
            href="/blog"
          >
            ← Back to articles
          </a>
        </div>

        {/* Cover image — wider than text column */}
        {post?.cover && (
          <div className="w-full max-w-3xl mb-8">
            <img
              src={post.cover}
              alt={post.title}
              className="w-full h-auto rounded object-cover max-h-[400px]"
            />
          </div>
        )}

        {/* Header */}
        <div className="w-full max-w-2xl mb-10">
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-3">
            {post?.title}
          </h1>

          <span className="text-sm text-gray-400">
            {new Date(post!.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>

          <div className="w-full h-px bg-gray-200 mt-6" />
        </div>

        {/* Article body */}
        <article className="prose-article w-full max-w-2xl">
          <ReactMarkdown
            components={{
              code({ node, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                return match ? (
                  <div className="border border-gray-200 rounded overflow-hidden my-6">
                    <div className="bg-gray-50 text-[10px] font-bold uppercase tracking-widest px-4 py-2 text-gray-400 border-b border-gray-200">
                      {match[1]}
                    </div>
                    <SyntaxHighlighter
                      style={oneLight}
                      language={match[1]}
                      className="!my-0 p-4 text-sm"
                      showLineNumbers
                    >
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  </div>
                ) : (
                  <code
                    className="bg-gray-100 text-stone-700 px-1.5 py-0.5 rounded text-sm"
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
            }}
          >
            {post?.content}
          </ReactMarkdown>
        </article>

        {/* Bottom nav */}
        <div className="w-full max-w-2xl mt-12 pt-6 border-t border-gray-200">
          <a
            href="/blog"
            className="text-sm font-bold text-orange-600 hover:text-orange-700 transition-colors"
          >
            ← All articles
          </a>
        </div>
      </div>
    </BaseContainer>
  );
}
